import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react'
import { Lock, RotateCw } from 'lucide-react'
import type { Step } from '../state/types.js'

/**
 * Mounts once at the App level. Listens for `veritact:browser-transition`
 * events dispatched via `startBrowserTransition(...)`. Renders an in-phone
 * "browser opened" animation (Mobile Safari-ish: traffic lights + URL pill +
 * loading sweep) that survives the underlying screen swap, so the next
 * screen materializes "behind the chrome" as if the link loaded a webpage.
 */

const TRANSITION_EVENT = 'veritact:browser-transition'
const STEP_CHANGED_EVENT = 'veritact:step-changed'

interface TransitionDetail {
  targetStep: Step
  targetUrl: string
}

type TransitionCustomEvent = CustomEvent<TransitionDetail>

type Phase = 'idle' | 'opening' | 'typing' | 'loading' | 'loaded' | 'closing'

/** Timeline (ms from event dispatch). Edit these to retune the transition. */
const TIMING = {
  toTyping: 380,
  toLoading: 720,
  swapScreen: 880,
  toLoaded: 980,
  toClosing: 2400,
  toIdle: 2800,
} as const

const useIsDesktop = (): boolean => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(min-width: 768px)').matches === true,
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia('(min-width: 768px)')
    const update = (): void => setIsDesktop(mql.matches)
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])
  return isDesktop
}

export const BrowserTransition = (): ReactElement | null => {
  const [phase, setPhase] = useState<Phase>('idle')
  const [entered, setEntered] = useState(false)
  const [url, setUrl] = useState('')
  const isDesktop = useIsDesktop()
  const timersRef = useRef<number[]>([])

  // Drive the chrome slide-in/out via a separate `entered` flag so the CSS
  // transition actually fires (a transform that's the same on first paint
  // and on subsequent renders doesn't animate; we need a value change).
  useEffect(() => {
    if (phase === 'opening') {
      const id = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(id)
    }
    if (phase === 'closing' || phase === 'idle') {
      setEntered(false)
    }
    return undefined
  }, [phase])

  useEffect(() => {
    const clearTimers = (): void => {
      for (const t of timersRef.current) window.clearTimeout(t)
      timersRef.current = []
    }

    const onTransition = (e: Event): void => {
      const ev = e as TransitionCustomEvent
      clearTimers()
      setUrl(ev.detail.targetUrl)
      setEntered(false)
      setPhase('opening')

      const schedule = (delay: number, fn: () => void): void => {
        timersRef.current.push(window.setTimeout(fn, delay))
      }

      schedule(TIMING.toTyping, () => setPhase('typing'))
      schedule(TIMING.toLoading, () => setPhase('loading'))
      schedule(TIMING.swapScreen, () => {
        const params = new URLSearchParams(window.location.search)
        params.set('step', ev.detail.targetStep)
        window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`)
        window.dispatchEvent(new Event(STEP_CHANGED_EVENT))
      })
      schedule(TIMING.toLoaded, () => setPhase('loaded'))
      schedule(TIMING.toClosing, () => setPhase('closing'))
      schedule(TIMING.toIdle, () => {
        setPhase('idle')
        timersRef.current = []
      })
    }

    window.addEventListener(TRANSITION_EVENT, onTransition)
    return () => {
      window.removeEventListener(TRANSITION_EVENT, onTransition)
      clearTimers()
    }
  }, [])

  if (phase === 'idle') return null

  // Match the AppShell phone-frame geometry on desktop, full mobile column otherwise.
  const containerStyle: CSSProperties = isDesktop
    ? {
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '334px',
        height: 'calc(min(852px, 92vh) - 16px)',
        borderRadius: '2.25rem',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 60,
      }
    : {
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '28rem',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 60,
      }

  // Chrome bar slides down from above the viewport. `entered` drives the
  // transition: false on first paint and on close, true between.
  const chromeStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    transform: entered ? 'translateY(0)' : 'translateY(-100%)',
    transition: 'transform 360ms cubic-bezier(0.22, 1, 0.36, 1)',
  }

  // White "page" backdrop dims the underlying email during reveal, then fades
  // out once the next screen has mounted so the new content shows through.
  const backdropVisible = phase === 'opening' || phase === 'typing' || phase === 'loading'
  const backdropStyle: CSSProperties = {
    position: 'absolute',
    top: '56px',
    left: 0,
    right: 0,
    bottom: 0,
    background: 'white',
    opacity: backdropVisible ? 1 : 0,
    transition: 'opacity 320ms ease-out',
  }

  // URL reveal width (percent of full)
  const urlReveal = phase === 'opening' ? 0 : 100

  // Loading bar progress
  const loadingActive = phase === 'loading' || phase === 'loaded'

  return (
    <div style={containerStyle} aria-hidden="true">
      <div style={backdropStyle} />
      <div style={chromeStyle}>
        <div className="bg-white/95 backdrop-blur-sm border-b border-black/10 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
          <div className="h-14 px-3 flex items-center gap-2">
            {/* Traffic lights */}
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
            </div>

            {/* URL pill */}
            <div className="flex-1 min-w-0 mx-1 h-8 rounded-lg bg-gray-100 border border-black/5 flex items-center gap-1.5 px-2.5 overflow-hidden">
              <Lock className="h-3.5 w-3.5 text-gray-500 shrink-0" />
              <div
                className="text-[12px] font-medium text-gray-700 whitespace-nowrap overflow-hidden"
                style={{
                  width: `${urlReveal}%`,
                  transition: 'width 380ms steps(28, end)',
                }}
              >
                {url}
              </div>
            </div>

            <RotateCw className="h-4 w-4 text-gray-500 shrink-0" />
          </div>

          {/* Loading bar */}
          <div className="relative h-[2px] bg-transparent">
            <div
              className="absolute inset-y-0 left-0 bg-[#1d4ed8]"
              style={{
                width: loadingActive ? '100%' : '0%',
                transition: loadingActive
                  ? 'width 600ms cubic-bezier(0.4, 0, 0.2, 1)'
                  : 'width 0ms',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Trigger the email → website transition.
 *
 * Desktop (>= 768px): runs the in-phone browser-chrome animation, then
 * navigates. The phone frame is a stylized mockup, so the chrome cue
 * sells the "this is now a website" beat.
 *
 * Mobile (< 768px): navigates directly. The user is already in a real
 * browser, so faking browser chrome inside it is meta and confusing.
 */
export const startBrowserTransition = (detail: TransitionDetail): void => {
  if (typeof window === 'undefined') return

  const isDesktop = window.matchMedia('(min-width: 768px)').matches
  if (!isDesktop) {
    const params = new URLSearchParams(window.location.search)
    params.set('step', detail.targetStep)
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`)
    window.dispatchEvent(new Event(STEP_CHANGED_EVENT))
    return
  }

  window.dispatchEvent(new CustomEvent<TransitionDetail>(TRANSITION_EVENT, { detail }))
}
